import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Modal, Form, InputGroup, Col, Row, CardBody, Card, ProgressBar, Alert} from 'react-bootstrap';
import {Button, useEnhancedTranslation} from 'laravel-inertia-backend';
import useTransmissionPolling from '../../hooks/useTransmissionPolling.js';

export default function ImportPositionsModal({show, documentType = 'sales_order', documentId, onClose, onAnalyzeResult}) {
    const {t} = useEnhancedTranslation();
    const [file, setFile] = useState(null);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [step, setStep] = useState(1); // 1 Upload, 2 Preview, 3 Import, 4 Done
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState('append'); // 'append' | 'replace'
    const [jobId, setJobId] = useState(null);
    const [progress, setProgress] = useState(0);
    const {poll} = useTransmissionPolling();

    // overrides
    const [delimiter, setDelimiter] = useState(''); // '', ',', ';', '\t', '|'
    const [encoding, setEncoding] = useState(''); // '', 'UTF-8', 'Windows-1252', ...
    const [articleIndex, setArticleIndex] = useState(null);
    const [quantityIndex, setQuantityIndex] = useState(null);
    const [extractBracket, setExtractBracket] = useState(null);

    const dropRef = useRef(null);
    const lastAnalyzeSigRef = useRef(null);
    const debounceTimerRef = useRef(null);

    const csrf = useMemo(() => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '', []);
    const accept = '.xlsx,.csv,.txt';

    // Ergebnis der Vorabprüfung gegen Weclapp. Unbekannte Nummern sperren die
    // Übernahme, weil Weclapp sonst den kompletten Beleg ablehnt.
    const unbekannteArtikel = result?.articleCheck?.unknown ?? [];
    const inaktiveArtikel = result?.articleCheck?.inactive ?? [];
    const artikelPruefungUebersprungen = result?.articleCheck?.skipped === true;
    const uebernahmeGesperrt = unbekannteArtikel.length > 0;

    const resetAll = () => {
        setFile(null);
        setError(null);
        setResult(null);
        setStep(1);
        setMode('append');
        setJobId(null);
        setProgress(0);
        setDelimiter('');
        setEncoding('');
        setArticleIndex(null);
        setQuantityIndex(null);
        setExtractBracket(null);
        lastAnalyzeSigRef.current = null;
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    };

    const handleFileChange = (f) => {
        setFile(f || null);
        setError(null);
        setResult(null);
        setStep(1);
        lastAnalyzeSigRef.current = null;
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.files?.length) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const buildAnalyzeFormData = () => {
        const form = new FormData();
        form.append('file', file);
        form.append('documentId', documentId);
        if (delimiter) form.append('delimiter', delimiter === '\t' ? "\t" : delimiter);
        if (encoding) form.append('encoding', encoding);
        if (articleIndex !== null && articleIndex !== undefined) form.append('articleIndex', String(articleIndex));
        if (quantityIndex !== null && quantityIndex !== undefined) form.append('quantityIndex', String(quantityIndex));
        if (extractBracket !== null && extractBracket !== undefined) form.append('extractBracket', extractBracket ? '1' : '0');
        return form;
    };

    const runAnalyze = async () => {
        if (!file) {
            setError('Bitte wählen Sie eine Datei (.xlsx oder .csv).');
            return;
        }
        if (!documentId) {
            setError('Kein Beleg ausgewählt. Bitte starten Sie den Import aus einer Tabellenzeile.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const form = buildAnalyzeFormData();
            const {data} = await axios.post('/weclapp/import/analyze', form, {
                headers: {'X-Requested-With': 'XMLHttpRequest', 'X-CSRF-TOKEN': csrf, 'Accept': 'application/json'},
                withCredentials: true
            });
            setResult(data);
            // initialize mapping overrides from response
            setArticleIndex(data?.mapping?.articleIndex ?? null);
            setQuantityIndex(data?.mapping?.quantityIndex ?? null);
            setExtractBracket(!!data?.mapping?.extractBracket);
            if (data?.detected?.type === 'csv') {
                const detDel = data?.detected?.delimiter || '';
                setDelimiter(detDel === '\t' ? '\t' : detDel);
                setEncoding(data?.detected?.encoding || '');
            } else {
                setDelimiter('');
                setEncoding('');
            }
            // update last signature to avoid immediate re-trigger
            const sig = JSON.stringify({
                delimiter: data?.detected?.type === 'csv' ? (data?.detected?.delimiter || '') : '',
                encoding: data?.detected?.type === 'csv' ? (data?.detected?.encoding || '') : '',
                articleIndex: data?.mapping?.articleIndex ?? null,
                quantityIndex: data?.mapping?.quantityIndex ?? null,
                extractBracket: !!data?.mapping?.extractBracket
            });
            lastAnalyzeSigRef.current = sig;
            onAnalyzeResult && onAnalyzeResult(data);
            setStep(2);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Analyse fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    const commitImport = async (ignoreUnknownArticles = false) => {
        if (!result) {
            return;
        }
        setStep(3);
        setLoading(true);
        setError(null);
        setProgress(0);
        try {
            const {data} = await axios.post('/weclapp/import/commit', {
                documentId,
                documentType,
                mode,
                ignoreUnknownArticles,
            });
            setJobId(data?.jobId ?? null);

            // Übertragung läuft als Worker-Job; Backend-Status pollen bis Abschluss.
            await poll(data.jobId, {
                onProgress: (s) => setProgress(s.progress_percent ?? 0),
            });

            setLoading(false);
            setStep(4);
        } catch (e) {
            setLoading(false);
            setError(e.response?.data?.message || e.message || 'Übernahme fehlgeschlagen');
            // allow retry: go back to step 2
            setStep(2);
        }
    };

    const retryImport = async () => {
        if (!jobId) return;
        setStep(3);
        setLoading(true);
        setError(null);
        setProgress(0);
        try {
            const {data} = await axios.post(`/weclapp/transmission/${jobId}/retry`);
            await poll(data.jobId, {
                onProgress: (s) => setProgress(s.progress_percent ?? 0),
            });
            setLoading(false);
            setStep(4);
        } catch (e) {
            setLoading(false);
            setError(e.response?.data?.message || e.message || 'Fortsetzen fehlgeschlagen');
            setStep(2);
        }
    };

    // Auto re-analyze on input changes in Step 2
    useEffect(() => {
        // Only auto-run in preview step with a file, a document, and after we have an initial result
        if (step !== 2) return;
        if (!file || !documentId) return;
        if (!result) return; // require one initial analyze via Step 1 button
        if (loading) return;

        const sig = JSON.stringify({
            delimiter,
            encoding,
            articleIndex,
            quantityIndex,
            extractBracket,
        });

        if (lastAnalyzeSigRef.current === sig) return;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            // set signature optimistically to prevent rapid re-triggers while request in flight
            lastAnalyzeSigRef.current = sig;
            runAnalyze();
        }, 400);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [step, file, documentId, delimiter, encoding, articleIndex, quantityIndex, extractBracket, loading, result]);

    const closeModal = () => {
        onClose && onClose();
        resetAll();
    };
    const csvOptions = (
        result?.detected?.type === 'csv' && (
            <Row className="g-2 mb-2">
                <Col xs={12} md={4}>
                    <Form.Label>Codierung</Form.Label>
                    <Form.Select value={encoding} onChange={(e) => setEncoding(e.target.value)}>
                        {['', 'UTF-8', 'Windows-1252', 'ISO-8859-1', 'ISO-8859-15'].map(opt => (
                            <option key={opt || 'auto'} value={opt}>{opt ? opt : 'Auto'}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col xs={12} md={4}>
                    <Form.Label>Trennzeichen</Form.Label>
                    <Form.Select value={delimiter} onChange={(e) => setDelimiter(e.target.value)}>
                        <option value="">Auto</option>
                        <option value=",">Komma (,)</option>
                        <option value=";">Semikolon (;)</option>
                        <option value="\t">Tab (\t)</option>
                        <option value="|">Pipe (|)</option>
                    </Form.Select>
                </Col>
            </Row>
        )
    );

    const mappingControls = result && (
        <>
        <Row className="g-2 mb-2">
            <Col xs={12} md={6}>
                <Form.Label>Artikel-Spalte</Form.Label>
                <Form.Select value={articleIndex ?? ''}
                             onChange={(e) => setArticleIndex(e.target.value === '' ? null : parseInt(e.target.value))}>
                    <option value="">Auto</option>
                    {(result.header || []).map((h, idx) => (
                        <option key={idx} value={idx}>{h || `Spalte ${idx + 1}`}</option>
                    ))}
                </Form.Select>
            </Col>
            <Col xs={12} md={6}>
                <Form.Label>Menge-Spalte</Form.Label>
                <Form.Select value={quantityIndex ?? ''}
                             onChange={(e) => setQuantityIndex(e.target.value === '' ? null : parseInt(e.target.value))}>
                    <option value="">Auto</option>
                    {(result.header || []).map((h, idx) => (
                        <option key={idx} value={idx}>{h || `Spalte ${idx + 1}`}</option>
                    ))}
                </Form.Select>
            </Col>
            </Row>
            <Row>
            <Col className="d-flex align-items-end">
                <Form.Check type="checkbox" label="aus Klammern extrahieren" checked={extractBracket}
                            onChange={(e) => setExtractBracket(e.target.checked)}/>
            </Col>
        </Row>
        </>
    );

    const UploadStep = (
        <div>
            <div className="mb-3">
                <InputGroup>
                    <Form.Control type="text" readOnly placeholder="Keine Datei ausgewählt"
                                  value={file ? file.name : ''}/>
                    <Button variant="secondary" onClick={() => dropRef.current?.click()}>Datei auswählen</Button>
                </InputGroup>
                <input ref={dropRef} type="file" accept={accept} className="d-none"
                       onChange={(e) => handleFileChange(e.target.files?.[0] || null)}/>
            </div>
            <div
                onDragOver={onDragOver}
                onDrop={onDrop}
                className="border border-secondary rounded p-3 text-center"
                style={{background: '#f8f9fa'}}
            >
                Datei hierher ziehen (Drag & Drop) oder über "Datei auswählen" wählen.
            </div>
        </div>
    );

    const PreviewStep = result && (
        <div>
            <Row>
                <Col>
                    <Card className={"h-100"}><Card.Body>
                        <div><strong>Zusammenfassung</strong></div>
                        <div>Gelesene Zeilen: {result.summary?.rowsRead ?? 0}</div>
                        <div>Importierte Positionen: {result.summary?.positions ?? 0}</div>
                        <div>Übersprungene Zeilen: {result.summary?.skipped ?? 0}</div>
                        <div>Aggregierte Duplikate: {result.summary?.aggregated ?? 0}</div>
                        <hr className="my-2"/>
                        <Form.Label className="mb-1"><strong>Vorhandene Positionen</strong></Form.Label>
                        <Form.Check
                            type="radio"
                            id="import-mode-append"
                            name="import-mode"
                            label="Positionen hinzufügen"
                            checked={mode === 'append'}
                            onChange={() => setMode('append')}
                        />
                        <Form.Check
                            type="radio"
                            id="import-mode-replace"
                            name="import-mode"
                            label="Vorhandene Positionen ersetzen"
                            checked={mode === 'replace'}
                            onChange={() => setMode('replace')}
                        />
                    </Card.Body></Card>
                </Col>
                {csvOptions && <Col>
                    <Card className={"h-100"}>
                        <Card.Body>
                            {csvOptions}
                        </Card.Body>
                    </Card>
                </Col>}
                <Col>
                    <Card className={"h-100"}>
                        <Card.Body>
                            {mappingControls}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {Array.isArray(result.errors) && result.errors.length > 0 && (
                <div className="mt-2">
                    <div><strong>Fehler</strong></div>
                    <ul>
                        {result.errors.map((e, i) => (<li key={i}>Zeile {e.row}: {e.reason}</li>))}
                    </ul>
                </div>
            )}

            {unbekannteArtikel.length > 0 && (
                <Alert variant="danger" className="mt-3 mb-0">
                    <Alert.Heading className="h6 mb-2">
                        {unbekannteArtikel.length === 1
                            ? 'Eine Artikelnummer ist in Weclapp nicht vorhanden'
                            : `${unbekannteArtikel.length} von ${result.positionsCount ?? 0} Artikelnummern sind in Weclapp nicht vorhanden`}
                    </Alert.Heading>
                    <ul className="mb-2">
                        {unbekannteArtikel.slice(0, 20).map((a, i) => (
                            <li key={i}>{a.row ? `Zeile ${a.row}: ` : ''}<strong>{a.article}</strong></li>
                        ))}
                        {unbekannteArtikel.length > 20 && (
                            <li>und {unbekannteArtikel.length - 20} weitere</li>
                        )}
                    </ul>
                    <div className="small mb-0">
                        Weclapp lehnt den gesamten Beleg ab, wenn eine Position nicht auflösbar ist.
                        Bitte die Nummern in der Datei korrigieren und erneut hochladen.
                    </div>
                </Alert>
            )}

            {inaktiveArtikel.length > 0 && (
                <Alert variant="warning" className="mt-3 mb-0">
                    <div className="mb-1">
                        <strong>
                            {inaktiveArtikel.length === 1
                                ? 'Ein Artikel ist in Weclapp inaktiv'
                                : `${inaktiveArtikel.length} Artikel sind in Weclapp inaktiv`}
                        </strong>
                    </div>
                    <ul className="mb-0">
                        {inaktiveArtikel.slice(0, 20).map((a, i) => (
                            <li key={i}>{a.row ? `Zeile ${a.row}: ` : ''}{a.article}</li>
                        ))}
                        {inaktiveArtikel.length > 20 && (
                            <li>und {inaktiveArtikel.length - 20} weitere</li>
                        )}
                    </ul>
                </Alert>
            )}

            {artikelPruefungUebersprungen && (
                <Alert variant="secondary" className="mt-3 mb-0">
                    Die Artikelnummern konnten nicht vorab gegen Weclapp geprüft werden.
                    Der Import ist weiterhin möglich; ein Fehler zeigt sich dann erst beim Übertragen.
                </Alert>
            )}

            <div className="mt-3">
                <div><strong>Vorschau – Original (erste 5 Zeilen)</strong></div>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                        <thead>
                        <tr>
                            {(result.header || []).map((h, idx) => (
                                <th key={idx} style={{whiteSpace: 'nowrap'}}>
                                    {h}
                                    {idx === (articleIndex ?? result?.mapping?.articleIndex) &&
                                        <span className="badge bg-success ms-2">Artikel</span>}
                                    {idx === (quantityIndex ?? result?.mapping?.quantityIndex) &&
                                        <span className="badge bg-success ms-2">Menge</span>}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {(result.sampleRows || []).map((row, rIdx) => (
                            <tr key={rIdx}>
                                {row.map((cell, cIdx) => {
                                    const isArticle = cIdx === (articleIndex ?? result?.mapping?.articleIndex);
                                    const isQty = cIdx === (quantityIndex ?? result?.mapping?.quantityIndex);
                                    const extractB = !!(extractBracket ?? result?.mapping?.extractBracket);

                                    const renderBracketHighlight = (value) => {
                                        const text = (value ?? '').toString();
                                        const open = text.lastIndexOf('(');
                                        const close = open >= 0 ? text.indexOf(')', open + 1) : -1;
                                        if (open >= 0 && close > open) {
                                            const before = text.slice(0, open + 1);
                                            const inner = text.slice(open + 1, close);
                                            const after = text.slice(close);
                                            return (
                                                <span>
                          {before}
                                                    <span style={{
                                                        background: '#d4edda',
                                                        borderRadius: '2px',
                                                        padding: '0 2px'
                                                    }}>{inner}</span>
                                                    {after}
                        </span>
                                            );
                                        }
                                        return text;
                                    };

                                    if (isArticle && extractB) {
                                        return (<td key={cIdx}>{renderBracketHighlight(cell)}</td>);
                                    }
                                    const highlight = isArticle || isQty;
                                    return (
                                        <td key={cIdx} style={highlight ? {background: '#d4edda'} : {}}>
                                            {cell}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="mt-3">
                <div><strong>Vorschau – Erkannt (erste 5 Zeilen)</strong></div>
                <div className="table-responsive">
                    <table className="table table-sm table-striped table-bordered">
                        <thead>
                        <tr>
                            <th>Artikel</th>
                            <th className="text-end">Menge</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(result.preview || []).map((p, i) => (
                            <tr key={i}>
                                <td>{p.article}</td>
                                <td className="text-end">{p.quantity}</td>
                            </tr>
                        ))}
                        {(!result.preview || result.preview.length === 0) && (
                            <tr>
                                <td colSpan={2} className="text-muted">Keine erkannten Zeilen in der Vorschau.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const ImportStep = (
        <div className="py-4">
            <div className="text-center mb-3">
                {mode === 'replace'
                    ? 'Vorhandene Positionen werden ersetzt…'
                    : 'Positionen werden an Weclapp übertragen…'}
            </div>
            <ProgressBar now={progress} label={`${progress}%`} animated striped/>
        </div>
    );

    const DoneStep = (
        <div className="text-center py-4">
            <div className="text-success fw-bold">Import abgeschlossen</div>
            <div className="mt-2">
                Es wurden {result?.summary?.positions ?? 0} Positionen
                {mode === 'replace' ? ' ersetzt.' : ' übernommen.'}
            </div>
        </div>
    );

    return (
        <Modal show={show} onHide={closeModal} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Positionen importieren</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="alert alert-danger">{error}</div>}

                {/* Step indicator */}
                <div className="mb-3">
                    <ol className="breadcrumb">
                        <li className={`breadcrumb-item ${step === 1 ? 'active' : ''}`}>1. Upload</li>
                        <li className={`breadcrumb-item ${step === 2 ? 'active' : ''}`}>2. Vorschau</li>
                        <li className={`breadcrumb-item ${step === 3 ? 'active' : ''}`}>3. Import</li>
                        <li className={`breadcrumb-item ${step === 4 ? 'active' : ''}`}>4. Bestätigung</li>
                    </ol>
                </div>

                {step === 1 && UploadStep}
                {step === 2 && PreviewStep}
                {step === 3 && ImportStep}
                {step === 4 && DoneStep}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
                {step === 1 && (
                    <Button variant="weclapp" onClick={runAnalyze} loading={loading}
                            disabled={!file}>Analysieren</Button>
                )}
                {step === 2 && (
                    <>
                        <Button variant="outline-secondary" onClick={() => setStep(1)}>Zurück</Button>
                        {jobId && (
                            <Button variant="warning" onClick={retryImport} disabled={loading}>Erneut senden</Button>
                        )}
                        {uebernahmeGesperrt && (
                            <Button variant="outline-danger" onClick={() => commitImport(true)} disabled={loading}
                                    title="Überträgt die Liste unverändert. Weclapp wird den Beleg voraussichtlich ablehnen.">
                                Trotzdem senden
                            </Button>
                        )}
                        <Button variant="weclapp" onClick={() => commitImport(false)}
                                disabled={loading || uebernahmeGesperrt}>Übernehmen</Button>
                    </>
                )}
                {step === 4 && (
                    <Button variant="weclapp" onClick={closeModal}>Schließen</Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
